export function flattenBlocksByType(ctx, blocksByType) {
	if (!blocksByType || typeof blocksByType !== 'object') {
		return [];
	}

	const flattened = [];

	for (const [blockType, entries] of Object.entries(blocksByType)) {
		if (!Array.isArray(entries)) {
			continue;
		}

		for (const entry of entries) {
			const canonicalBlockType = canonicalizeBlockType(ctx, blockType);
			flattened.push({
				index: Number.isInteger(entry?.index) ? entry.index : flattened.length,
				selector: typeof entry?.selector === 'string' ? entry.selector : '',
				blockType,
				canonicalBlockType,
				textPreview: typeof entry?.textPreview === 'string' ? entry.textPreview : '',
				tag: typeof entry?.tag === 'string' ? entry.tag : '',
				id: typeof entry?.id === 'string' ? entry.id : '',
				className: typeof entry?.className === 'string' ? entry.className : '',
				failedRuleKeys: ctx.normalizeRuleKeys(entry?.failedRuleKeys),
				documentIndex: Number.isInteger(entry?.documentIndex) ? entry.documentIndex : 0
			});
		}
	}

	return flattened;
}

export function buildEditorCanvasBlocksMap(ctx) {
	const rulesPreviewIframe = ctx.getRulesPreviewIframe();

	if (!rulesPreviewIframe || !rulesPreviewIframe.contentDocument) {
		return {
			ok: false,
			reason: 'rules_view_not_ready',
			totalBlocks: 0,
			blocks: [],
			documents: []
		};
	}

	const editorDocuments = ctx.collectEditorCanvasDocuments(rulesPreviewIframe.contentDocument);

	if (editorDocuments.length === 0) {
		return {
			ok: false,
			reason: 'editor_canvas_not_ready',
			totalBlocks: 0,
			blocks: [],
			documents: []
		};
	}

	const blocks = [];
	const documents = [];

	for (const [documentIndex, editorDocument] of editorDocuments.entries()) {
		documents.push(ctx.describeTargetDocument(editorDocument, documentIndex));
		const candidates = Array.from(editorDocument.querySelectorAll('[class*="wp-block-"], p'));

		for (const node of candidates) {
			if (!node || node.nodeType !== 1 || !node.classList) {
				continue;
			}

			const blockClassName = Array.from(node.classList).find((className) => className.startsWith('wp-block-'));
			const isParagraphTag = node.tagName.toLowerCase() === 'p';

			if (!blockClassName && !isParagraphTag) {
				continue;
			}

			const blockType = blockClassName
				? (blockClassName.replace(/^wp-block-/, '').trim() || 'unknown')
				: 'paragraph_tag';
			const canonicalBlockType = canonicalizeBlockType(ctx, blockType);

			blocks.push({
				node,
				documentIndex,
				blockType,
				canonicalBlockType,
				tag: node.tagName.toLowerCase(),
				selector: buildNodeSelector(node),
				id: node.id || '',
				className: node.className || '',
				textNormalized: ctx.normalizeComparableText(node.textContent || ''),
				textPreview: ctx.buildTextPreview(node.textContent || '', 120)
			});
		}
	}

	return {
		ok: true,
		reason: '',
		totalBlocks: blocks.length,
		blocks,
		documents
	};
}

export function canonicalizeBlockType(_ctx, rawBlockType) {
	const blockType = String(rawBlockType || '').trim().toLowerCase();

	if (!blockType) {
		return 'unknown';
	}

	if (blockType === 'paragraph_tag') {
		return 'paragraph';
	}

	if (blockType.startsWith('post-content')) {
		return 'post-content';
	}

	return blockType.replace(/-is-layout-[a-z0-9-]+$/i, '');
}

export function scoreLiveToEditorBlockMatch(ctx, liveBlock, editorBlock) {
	if (!liveBlock || !editorBlock) {
		return 0;
	}

	const liveType = canonicalizeBlockType(ctx, liveBlock.canonicalBlockType || liveBlock.blockType);
	const editorType = canonicalizeBlockType(ctx, editorBlock.canonicalBlockType || editorBlock.blockType);

	if (liveType !== editorType) {
		return 0;
	}

	const liveText = ctx.normalizeComparableText(liveBlock.textPreview || '');
	const editorText = ctx.normalizeComparableText(editorBlock.textPreview || '');
	const liveCompactText = ctx.normalizeCompactComparableText(liveText);
	const editorCompactText = ctx.normalizeCompactComparableText(editorText);
	let score = 1;

	if (liveText && editorText) {
		if (liveText === editorText) {
			score += 4;
		} else if (liveCompactText && editorCompactText && liveCompactText === editorCompactText) {
			score += 3;
		} else if (liveText.length >= 20 && (editorText.includes(liveText) || liveText.includes(editorText))) {
			score += 3;
		} else if (
			liveCompactText.length >= 16
			&& editorCompactText.length >= 16
			&& (editorCompactText.includes(liveCompactText) || liveCompactText.includes(editorCompactText))
		) {
			score += 2;
		} else if (ctx.hasStrongTokenOverlap(liveText, editorText, 2)) {
			score += 2;
		}
	}

	if (liveBlock.tag && editorBlock.tag && liveBlock.tag === editorBlock.tag) {
		score += 1;
	}

	if (!liveText && !editorText) {
		score += 1;
	}

	return score;
}

export function resolveFirstPostContentIndex(ctx, liveBlocks) {
	if (!Array.isArray(liveBlocks) || liveBlocks.length === 0) {
		return -1;
	}

	const postContentBlock = liveBlocks.find((block) => canonicalizeBlockType(ctx, block?.canonicalBlockType || block?.blockType || '') === 'post-content');

	return Number.isInteger(postContentBlock?.index) ? postContentBlock.index : -1;
}

export function shouldIncludeLiveBlockForEditorMapping(ctx, liveBlock, firstPostContentIndex) {
	if (!liveBlock) {
		return false;
	}

	if (liveBlock.id === 'wp--skip-link--target') {
		return false;
	}

	const blockType = canonicalizeBlockType(ctx, liveBlock.canonicalBlockType || liveBlock.blockType || '');
	const isPreContent = Number.isInteger(liveBlock.index)
		&& Number.isInteger(firstPostContentIndex)
		&& firstPostContentIndex >= 0
		&& liveBlock.index < firstPostContentIndex;

	if (isPreContent && (blockType === 'group' || blockType === 'spacer')) {
		return false;
	}

	return true;
}

export function resolveBlockMatchThreshold(ctx, liveBlock) {
	const blockType = canonicalizeBlockType(ctx, liveBlock?.canonicalBlockType || liveBlock?.blockType || '');
	const text = ctx.normalizeComparableText(liveBlock?.textPreview || '');

	if (!text) {
		return 2;
	}

	if (blockType === 'group' || blockType === 'column' || blockType === 'columns') {
		return 4;
	}

	return 3;
}

export function shouldHighlightMappedEditorBlock(ctx, liveBlock) {
	if (!liveBlock || typeof liveBlock.className !== 'string') {
		return false;
	}

	const hasLiveFailureClass = liveBlock.className.split(/\s+/).includes('a11yfix-rule-fail-highlight');
	const failedRuleKeys = ctx.normalizeRuleKeys(liveBlock.failedRuleKeys);

	if (failedRuleKeys.length === 0) {
		return hasLiveFailureClass;
	}

	if (!ctx.activeRuleHighlightKeys || ctx.activeRuleHighlightKeys.size === 0) {
		return false;
	}

	return failedRuleKeys.some((ruleKey) => ctx.activeRuleHighlightKeys.has(ruleKey));
}

export function mapLiveBlocksToEditorAndHighlight(ctx, liveMap) {
	const allLiveBlocks = flattenBlocksByType(ctx, liveMap?.blocksByType || {});
	const firstPostContentIndex = resolveFirstPostContentIndex(ctx, allLiveBlocks);
	const liveBlocks = allLiveBlocks.filter((liveBlock) => shouldIncludeLiveBlockForEditorMapping(ctx, liveBlock, firstPostContentIndex));
	const ignoredLiveBlocks = allLiveBlocks.filter((liveBlock) => !shouldIncludeLiveBlockForEditorMapping(ctx, liveBlock, firstPostContentIndex));
	const editorMap = buildEditorCanvasBlocksMap(ctx);
	clearLiveToEditorMatchHighlights(ctx);

	if (!editorMap.ok) {
		const unavailable = {
			ok: false,
			reason: editorMap.reason || 'editor_canvas_unavailable',
			allLiveBlocksCount: allLiveBlocks.length,
			liveBlocksCount: liveBlocks.length,
			ignoredLiveBlocksCount: ignoredLiveBlocks.length,
			editorBlocksCount: 0,
			liveBlocks,
			ignoredLiveBlocks,
			editorBlocks: [],
			matchedCount: 0,
			matches: []
		};
		return unavailable;
	}

	const editorBlocks = editorMap.blocks;
	const editorBlocksForTuning = editorBlocks.map((block) => ({
		documentIndex: block.documentIndex,
		blockType: block.blockType,
		selector: block.selector,
		tag: block.tag,
		id: block.id,
		className: block.className,
		textPreview: block.textPreview
	}));
	const usedEditorIndexes = new Set();
	const matches = [];
	let highlightedMatchedCount = 0;
	let lastMatchedEditorIndex = -1;
	const selectorsByDocumentIndex = new Map();

	for (const liveBlock of liveBlocks) {
		let bestEditorIndex = -1;
		let bestScore = 0;
		let secondBestScore = 0;
		let candidateCount = 0;
		const threshold = resolveBlockMatchThreshold(ctx, liveBlock);

		const evaluateCandidate = (editorIndex) => {
			if (usedEditorIndexes.has(editorIndex)) {
				return;
			}

			const editorBlock = editorBlocks[editorIndex];
			const score = scoreLiveToEditorBlockMatch(ctx, liveBlock, editorBlock);
			candidateCount += 1;

			if (score > bestScore) {
				secondBestScore = bestScore;
				bestScore = score;
				bestEditorIndex = editorIndex;
				return;
			}

			if (score > secondBestScore) {
				secondBestScore = score;
			}
		};

		for (let editorIndex = lastMatchedEditorIndex + 1; editorIndex < editorBlocks.length; editorIndex += 1) {
			evaluateCandidate(editorIndex);
		}

		if (bestEditorIndex < 0 || bestScore < threshold) {
			for (let editorIndex = 0; editorIndex < editorBlocks.length; editorIndex += 1) {
				evaluateCandidate(editorIndex);
			}
		}

		if (bestEditorIndex < 0 || bestScore < threshold) {
			continue;
		}

		usedEditorIndexes.add(bestEditorIndex);
		lastMatchedEditorIndex = bestEditorIndex;
		const matchedEditorBlock = editorBlocks[bestEditorIndex];
		const shouldHighlight = shouldHighlightMappedEditorBlock(ctx, liveBlock);

		if (shouldHighlight) {
			const highlightSelector = getLiveEditorMatchSelectorForNode(ctx, matchedEditorBlock.node);

			if (highlightSelector) {
				ctx.setLiveEditorMatchHoverData(matchedEditorBlock.node, liveBlock);
				const documentSelectors = selectorsByDocumentIndex.get(matchedEditorBlock.documentIndex) || new Set();
				documentSelectors.add(highlightSelector);
				selectorsByDocumentIndex.set(matchedEditorBlock.documentIndex, documentSelectors);
				highlightedMatchedCount += 1;
			}
		}

		matches.push({
			liveIndex: Number.isInteger(liveBlock.index) ? liveBlock.index : -1,
			editorIndex: bestEditorIndex,
			blockType: liveBlock.blockType,
			canonicalBlockType: liveBlock.canonicalBlockType,
			threshold,
			score: bestScore,
			scoreGap: Math.max(0, bestScore - secondBestScore),
			candidateCount,
			highlighted: shouldHighlight,
			live: {
				selector: String(liveBlock.selector || ''),
				tag: liveBlock.tag,
				id: liveBlock.id,
				textPreview: liveBlock.textPreview,
				className: liveBlock.className
			},
			editor: {
				documentIndex: matchedEditorBlock.documentIndex,
				selector: matchedEditorBlock.selector,
				tag: matchedEditorBlock.tag,
				id: matchedEditorBlock.id,
				textPreview: matchedEditorBlock.textPreview,
				className: matchedEditorBlock.className
			}
		});
	}

	const matchedLiveIndexes = new Set(matches.map((match) => match.liveIndex).filter((index) => Number.isInteger(index) && index >= 0));
	const matchedEditorIndexes = new Set(matches.map((match) => match.editorIndex).filter((index) => Number.isInteger(index) && index >= 0));
	const unmatchedLiveBlocks = liveBlocks
		.filter((block) => !matchedLiveIndexes.has(block.index))
		.map((block) => ({
			index: block.index,
			documentIndex: block.documentIndex,
			blockType: block.blockType,
			canonicalBlockType: block.canonicalBlockType,
			selector: block.selector,
			tag: block.tag,
			id: block.id,
			className: block.className,
			textPreview: block.textPreview,
			failedRuleKeys: block.failedRuleKeys
		}));
	const unmatchedEditorBlocks = editorBlocks
		.filter((_block, editorIndex) => !matchedEditorIndexes.has(editorIndex))
		.map((block, editorIndex) => ({
			index: editorIndex,
			documentIndex: block.documentIndex,
			blockType: block.blockType,
			canonicalBlockType: block.canonicalBlockType,
			selector: block.selector,
			tag: block.tag,
			id: block.id,
			className: block.className,
			textPreview: block.textPreview
		}));

	for (const [documentIndex, selectorsSet] of selectorsByDocumentIndex.entries()) {
		const targetDocument = editorBlocks.find((block) => block.documentIndex === documentIndex)?.node?.ownerDocument;

		if (!targetDocument) {
			continue;
		}

		setLiveToEditorMatchStyle(ctx, targetDocument, Array.from(selectorsSet));
	}

	const result = {
		ok: true,
		reason: '',
		allLiveBlocksCount: allLiveBlocks.length,
		liveBlocksCount: liveBlocks.length,
		ignoredLiveBlocksCount: ignoredLiveBlocks.length,
		editorBlocksCount: editorBlocks.length,
		liveBlocks,
		ignoredLiveBlocks,
		editorBlocks: editorBlocksForTuning,
		matchedCount: matches.length,
		highlightedMatchedCount,
		unmatchedLiveCount: Math.max(0, liveBlocks.length - matches.length),
		unmatchedEditorCount: Math.max(0, editorBlocks.length - matches.length),
		coverage: liveBlocks.length > 0 ? Number((matches.length / liveBlocks.length).toFixed(4)) : 0,
		documents: editorMap.documents,
		matches,
		unmatchedLiveBlocks,
		unmatchedEditorBlocks
	};

	return result;
}

function buildNodeSelector(node) {
	if (!node || node.nodeType !== 1) {
		return '';
	}

	const tag = String(node.tagName || 'div').toLowerCase();
	const id = String(node.id || '').trim();

	if (id) {
		return `${tag}#${escapeSelectorToken(id)}`;
	}

	const classTokens = String(node.className || '')
		.split(/\s+/)
		.map((token) => token.trim())
		.filter((token) => token.length > 0)
		.slice(0, 3)
		.map((token) => `.${escapeSelectorToken(token)}`)
		.join('');

	const position = resolveNthOfTypePosition(node);
	const nthSegment = position > 0 ? `:nth-of-type(${position})` : '';

	return `${tag}${classTokens}${nthSegment}`;
}

function resolveNthOfTypePosition(node) {
	if (!node?.parentElement) {
		return 1;
	}

	const sameTagSiblings = Array.from(node.parentElement.children).filter((child) => {
		return child?.tagName?.toLowerCase?.() === node.tagName.toLowerCase();
	});

	const index = sameTagSiblings.indexOf(node);
	return index >= 0 ? index + 1 : 1;
}

function escapeSelectorToken(value) {
	return String(value || '').replace(/([^a-zA-Z0-9_-])/g, '\\$1');
}

export function clearLiveToEditorMatchHighlights(ctx) {
	const rulesPreviewIframe = ctx.getRulesPreviewIframe();

	if (!rulesPreviewIframe || !rulesPreviewIframe.contentDocument) {
		return 0;
	}

	const rootDocument = rulesPreviewIframe.contentDocument;
	const targetDocuments = ctx.getHighlightTargetDocuments(rootDocument, true);
	let clearedCount = 0;

	for (const targetDocument of targetDocuments) {
		ctx.teardownLiveEditorHoverPopupBinding(targetDocument);
		const styleNode = targetDocument.getElementById('a11yfix-live-editor-match-highlight-style');

		if (styleNode) {
			styleNode.remove();
			clearedCount += 1;
		}

		const popupNodes = targetDocument.querySelectorAll('[data-a11yfix-rule-popup], [data-a11yfix-rule-keys]');

		for (const popupNode of popupNodes) {
			popupNode.removeAttribute('data-a11yfix-rule-popup');
			popupNode.removeAttribute('data-a11yfix-rule-popup-json');
			popupNode.removeAttribute('data-a11yfix-rule-keys');
		}
	}

	return clearedCount;
}

export function generateA11yFixUuid(_ctx) {
	if (window.crypto && typeof window.crypto.randomUUID === 'function') {
		return window.crypto.randomUUID();
	}

	const randomPart = Math.random().toString(36).slice(2, 10);
	return `a11yfix-${Date.now().toString(36)}-${randomPart}`;
}

export function getLiveEditorMatchSelectorForNode(ctx, node) {
	if (!node || node.nodeType !== 1 || typeof node.getAttribute !== 'function') {
		return '';
	}

	const dataBlockValue = (node.getAttribute('data-block') || '').trim();

	if (dataBlockValue) {
		return `[data-block="${ctx.escapeSelectorValue(dataBlockValue)}"]`;
	}

	let dataA11yFixValue = (node.getAttribute('data-a11fix') || '').trim();

	if (!dataA11yFixValue) {
		dataA11yFixValue = generateA11yFixUuid(ctx);
		node.setAttribute('data-a11fix', dataA11yFixValue);
	}

	return `[data-a11fix="${ctx.escapeSelectorValue(dataA11yFixValue)}"]`;
}

export function setLiveToEditorMatchStyle(ctx, documentNode, selectors) {
	if (!documentNode) {
		return;
	}

	const styleId = 'a11yfix-live-editor-match-highlight-style';
	let styleNode = documentNode.getElementById(styleId);

	if (!Array.isArray(selectors) || selectors.length === 0) {
		if (styleNode) {
			styleNode.remove();
		}
		ctx.hideLiveEditorHoverPopup(documentNode);
		return;
	}

	if (!styleNode) {
		styleNode = documentNode.createElement('style');
		styleNode.id = styleId;
		documentNode.head.appendChild(styleNode);
	}

	const selectorList = selectors.join(',');
	styleNode.textContent = `${selectorList}{outline:2px solid #d63638 !important;outline-offset:2px !important;background-color:rgba(214,54,56,0.12) !important;}`;
	ctx.ensureLiveEditorHoverPopupBinding(documentNode);
}
